/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useMemo, ComponentType } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Edit, Trash, ListSettings, ListUser, PlaceBuildingOffice } from '@jda/lui-common-icon-library-mui5';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  SvgIconProps,
  TableCellProps,
  Tooltip,
  Typography
} from '@mui/material';
import { LuiThreeDotMenu, LuiEmptyState, LuiMaterialTableHeadCellStyles } from '@jda/lui-common-component-library-mui5';
import moment from 'moment';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import { useStyles } from './styles';
import { LABEL, EDIT_USER, DELETE_USER, MANAGE_ROLES, ACTIONS, ASSIGNED_ROLES, MANAGE_LOCATION } from './constants';
import { IUserList } from '../../models/Users/UserContracts';
import { ROWS_PER_PAGE_LABEL } from '../RoleDetails/constants';
import useWindowDimensions from '../../utility/useWindowDimensions';
import CustomTablePagination from '../../shared/CustomTablePagination';
import { getPaginationDetails, getAllIdps, selectLocationIdpId } from '../../store/user/userSelector';
import { checkPermission } from '../../shared/CheckPermissions/CheckPermission';
import { permissions } from '../../shared/CheckPermissions/constants';
import { selectAllAssignedPermissions } from '../../store/auth/authSelector';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { ASSIGNED_LOCATIONS } from '../../container/Users/constants';
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';
import { selectRealmSettings } from '../../store/realmSettings/realmSettingsSelector';
import { NO_RESULT_FOUND, PLEASE_TRY_A_DIFFERENT_USER } from '../../container/constants';

const UserTable = ({
  filteredUsers,
  handleDeleteUser,
  handleListItemClick,
  handleManageRoles,
  handleAssignLocation,
  handleManagePopoverContent,
  selectedRowsData,
  onSelectedRowsDataChange,
  onChangePageOptions,
  extendedToolbar,
  loading
}: IUserList) => {
  const { classes } = useStyles();
  const history = useHistory();
  const { height } = useWindowDimensions();

  const selectGetAllIdps = useSelector(getAllIdps);
  const selectedPagination = useSelector(getPaginationDetails);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const appLocation = useSelector(selectAppLocation);
  const locationIdpId = useSelector(selectLocationIdpId);
  const isLocationEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.location);
  const realmSettings = useSelector(selectRealmSettings);

  const idpMap = useMemo(() => new Map<string, any>(selectGetAllIdps.map((idp: any) => [idp.id, idp])), [
    selectGetAllIdps
  ]);

  const userReadWritePermission = Boolean(
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.user.read_write], assignedPermissions.permissions)
  );
  const roleAssignPermission = Boolean(
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.subject.read_assign], assignedPermissions.permissions)
  );

  const isRoleAssignmentAllowed = userReadWritePermission && roleAssignPermission;

  const renderThreeDotMenuItem = (label: string, icon: ComponentType<SvgIconProps>) => {
    const Icon = icon;

    return (
      <>
        <ListItemIcon className={classes.actionIcon}>
          <Icon fontSize="small" className={classes.icon} />
        </ListItemIcon>
        <ListItemText>{label}</ListItemText>
      </>
    );
  };

  const columns = [
    {
      header: LABEL.USER_NAME,
      accessorKey: 'userName',
      enableSorting: true,
      muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '20%'
        })
      } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.userName}
          placement="bottom"
          disableHoverListener={!row.original.userName}
          disableFocusListener={!row.original.userName}
        >
          <Typography variant="body1" noWrap>
            {row.original.userName}
          </Typography>
        </Tooltip>
      )
    },
    {
      header: LABEL.NAME,
      accessorKey: 'displayName',
      enableSorting: true,
      muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '15%'
        })
      } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.displayName || LABEL.NO_VALUE}
          placement="bottom"
          disableHoverListener={!row.original.displayName}
          disableFocusListener={!row.original.displayName}
        >
          <Typography variant="body1" noWrap>
            {row.original.displayName || LABEL.NO_VALUE}
          </Typography>
        </Tooltip>
      )
    },
    {
      header: LABEL.EMAIL,
      accessorKey: 'email',
      enableSorting: true,
      muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '20%'
        })
      } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.email || LABEL.NO_VALUE}
          placement="bottom"
          disableHoverListener={!row.original.email}
          disableFocusListener={!row.original.email}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.email || LABEL.NO_VALUE}
          </Typography>
        </Tooltip>
      )
    },
    {
      header: LABEL.IDP_PROVIDER,
      accessorKey: 'idpId',
      enableSorting: true,
      muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '10%'
        })
      } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => {
        const idp = idpMap.get(row.original.idpId);
        const idpName = idp?.name ?? LABEL.NO_VALUE;
        return (
          <Tooltip arrow title={idpName} placement="bottom" disableHoverListener={!idp} disableFocusListener={!idp}>
            <Typography variant="body1" color="textPrimary" noWrap>
              {idpName}
            </Typography>
          </Tooltip>
        );
      }
    },
    userReadWritePermission
      ? [
        {
          header: LABEL.LAST_LOGIN_AT,
          accessorKey: 'lastLoginAt',
          enableSorting: true,
          muiTableHeadCellProps: ({ column, table }) =>
          ({
            sx: theme => ({
              ...LuiMaterialTableHeadCellStyles({
                column,
                theme,
                table,
                styling: 'standard',
                virtualizationEnabled: false
              }),
              width: '20%'
            })
          } as TableCellProps),
          Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => {
            const lastLoginStr = moment(row.original.lastLoginAt);
            const lastlogin = row.original.lastLoginAt
              ? `${lastLoginStr.format('MMM Do YYYY, h:mm:ss A')} GMT`
              : LABEL.NO_VALUE;
            return (
              <Tooltip
                arrow
                title={lastlogin}
                placement="bottom"
                disableHoverListener={!row.original.lastLoginAt}
                disableFocusListener={!row.original.lastLoginAt}
              >
                <Typography variant="body1" color="textPrimary" noWrap>
                  {lastlogin}
                </Typography>
              </Tooltip>
            );
          }
        }
      ]
      : [],
    {
      header: '',
      accessorKey: 'roles',
      enableSorting: false,
      size: 30,
      muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          marginRight: 10
        })
      } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <div>
          <Tooltip arrow title={ASSIGNED_ROLES} placement="bottom">
            <div>
              <IconButton
                size="small"
                onClick={(e: any) => handleManagePopoverContent(e, row.original.id, 'roles')}
                data-testid={`user-roles-assigned-${row.original.id}`}
                className={`user-roles-assigned-info-button`}
              >
                <ListUser color="secondary" className={classes.leftArrowIcon} />
              </IconButton>
            </div>
          </Tooltip>
        </div>
      )
    },
    isLocationEnabled && realmSettings?.enableLocationSegregation
      ? [
        {
          header: '',
          accessorKey: 'locations',
          enableSorting: false,
          size: 30,
          muiTableHeadCellProps: ({ column, table }) =>
          ({
            sx: theme => ({
              ...LuiMaterialTableHeadCellStyles({
                column,
                theme,
                table,
                styling: 'standard',
                virtualizationEnabled: false
              }),
              marginRight: 10
            })
          } as TableCellProps),
          Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
            <div>
              <Tooltip arrow title={ASSIGNED_LOCATIONS} placement="bottom">
                <div>
                  <IconButton
                    size="small"
                    onClick={(e: any) => handleManagePopoverContent(e, row.original.id, 'locations')}
                    data-testid={`user-locations-assigned-${row.original.id}`}
                    className={`user-locations-assigned-info-button`}
                  >
                    <BusinessOutlinedIcon color="secondary" className={classes.leftArrowIcon} />
                  </IconButton>
                </div>
              </Tooltip>
            </div>
          )
        }
      ]
      : [],
    userReadWritePermission
      ? [
        {
          header: '',
          accessorKey: 'menu',
          enableSorting: false,
          size: 30,
          muiTableHeadCellProps: {
            sx: {
              padding: 0,
              color: 'text.primary'
            }
          },
          muiTableBodyCellProps: {
            sx: {
              padding: 0
            }
          },
          Cell: ({ row }: { row: any }) => {
            const isEditAndDeleteEnabled = !appLocation || row.original.idpId === locationIdpId;

            const items = [
              isEditAndDeleteEnabled
                ? [
                  {
                    render: () => renderThreeDotMenuItem(EDIT_USER, Edit),
                    onClick: () => {
                      handleListItemClick(row);
                      if (history.location.search) {
                        history.push(`/userDetail/id=${row.id}${history.location.search}`);
                      } else {
                        history.push(`/userDetail/${row.id}`);
                      }
                    }
                  }
                ]
                : [],
              isEditAndDeleteEnabled
                ? [
                  {
                    render: () => renderThreeDotMenuItem(DELETE_USER, Trash),
                    onClick: () => {
                      const idp = selectGetAllIdps.find((idp: any) => idp.id === row.original.idpId);
                      handleDeleteUser(row.original, idp);
                    },
                    autoClose: true
                  }
                ]
                : [],
              isRoleAssignmentAllowed
                ? [
                  {
                    render: () => renderThreeDotMenuItem(MANAGE_ROLES, ListSettings),
                    onClick: () => {
                      handleManageRoles(row.original.id);
                    },
                    autoClose: true
                  }
                ]
                : [],
              isLocationEnabled && realmSettings?.enableLocationSegregation && !appLocation
                ? [
                  {
                    render: () => renderThreeDotMenuItem(MANAGE_LOCATION, PlaceBuildingOffice),
                    onClick: () => {
                      handleAssignLocation(row.original.id);
                    },
                    autoClose: true
                  }
                ]
                : []
            ].flat();

            return items.length > 0 ? <LuiThreeDotMenu items={items} ariaLabel={ACTIONS} /> : null;
          }
        }
      ]
      : []
  ].flat();
  const emptyTableFallback = () => {
    return (
      <Box my={5}>
        <LuiEmptyState imageItem={null} heading={NO_RESULT_FOUND} subHeading={PLEASE_TRY_A_DIFFERENT_USER} />
      </Box>
    );
  };
  return (
    <Box className={classes.userListWrap}>
      <CustomTablePagination
        columns={columns}
        data={filteredUsers}
        enableBottomToolbar={false}
        enableColumnsToggle={true}
        enableColumnActions={false}
        enableSortingRemoval={false}
        maxTableBodyHeight={height - 201}
        rowsPerPageOptions={[20, 50, 100]}
        selectedRowsData={selectedRowsData}
        sortingOptions={{ sorting: true }}
        enableMultiSort={false}
        loading={loading}
        totalCount={selectedPagination.count}
        labelRowsPerPage={ROWS_PER_PAGE_LABEL}
        onChangePageOptions={onChangePageOptions}
        enableRowSelection={isRoleAssignmentAllowed}
        currentPageNumber={selectedPagination.pageNumber}
        onSelectedRowsDataChange={onSelectedRowsDataChange}
        currentItemsPerPage={selectedPagination.pageSize || 50}
        renderTopToolbar={extendedToolbar}
        renderEmptyState={emptyTableFallback}
      />
    </Box>
  );
};

export default UserTable;
